import type { ToneNotation } from "@kensio/pinyinjs";

import type { CopyFlavours } from "./clipboard.js";
import { plainFlavours } from "./copy-html.js";
import { hskBandLevel } from "../content/hsk/hsk-band.js";
import type { SegmentedWord } from "../segmenting/segmented-words.js";
import { markPinyinWord } from "./syllable-text.js";
import { orderedWords, type WordOrder } from "../segmenting/word-order.js";
import { frequencyBandOf } from "../segmenting/word-frequency.js";

/**
 * The word list as something to paste.
 *
 * Out of the same words and the same bands as the table, so that what somebody
 * pastes is the list they were looking at. Apart from `segmenter-rows.ts`
 * because a table cell and a line of text have almost nothing in common beyond
 * that. One is markup a browser lays out and the other is what survives being
 * pasted into a program that knows nothing about this page.
 *
 * **The two no longer hold the same columns, and that is deliberate.** How
 * common a word is in general lost its column when the meanings arrived, the
 * table having a width and a spreadsheet having none, and it stays here. The
 * meanings go the other way. They arrive one word at a time after the list is
 * drawn, so a reader pressing Copy while they are still coming in would paste
 * a column that was full at the top and empty at the bottom. A spreadsheet
 * holding the words is a page away from what each of them means.
 */

/**
 * The list as tab separated text, for a spreadsheet or a flashcard deck.
 *
 * Tabs rather than commas, because a comma inside a gloss would need quoting
 * and every program that takes a pasted table takes tabs. A header row, so
 * that five unlabelled columns arriving in somebody's spreadsheet still say
 * what they are.
 *
 * The 词典 link is not among them. A reader pasting into a spreadsheet is
 * taking the words away, and a column of addresses to a site they have left is
 * not what they came for.
 */
export function wordsAsText(
  words: readonly SegmentedWord[],
  notation: ToneNotation,
): string {
  const rows = words.map((word) =>
    [
      word.text,
      markPinyinWord(word.reading, notation).join(""),
      String(word.count),
      frequencyBandOf(word.frequency)?.name ?? "",
      word.band === undefined ? "" : hskBandLevel(word.band),
    ].join("\t"),
  );

  return ["Word\tPinyin\tCount\tHow common\tHSK", ...rows].join("\n");
}

/**
 * The list as something to put on the clipboard, or nothing to copy.
 *
 * Undefined for an empty list rather than a header row on its own, so that a
 * copy button pressed before anything has been read does nothing instead of
 * putting five column names on the clipboard.
 */
export function wordsCopy(
  words: readonly SegmentedWord[],
  notation: ToneNotation,
): CopyFlavours | undefined {
  return words.length === 0
    ? undefined
    : plainFlavours(wordsAsText(words, notation));
}

/**
 * The list as the page is showing it, ready for the clipboard.
 *
 * Ordered here rather than by the caller, so that a spreadsheet somebody
 * pastes into arrives sorted the way the page was.
 */
export function shownWordsCopy(
  words: readonly SegmentedWord[],
  order: WordOrder,
  notation: ToneNotation,
): CopyFlavours | undefined {
  return wordsCopy(orderedWords(words, order), notation);
}
